import { Truncate } from "@components/Truncate";
import { invenioConfig } from "@config";
import LiteratureCover from "@modules/Literature/LiteratureCover";
import LiteratureTags from "@modules/Literature/LiteratureTags";
import LiteratureTitle from "@modules/Literature/LiteratureTitle";
import { FrontSiteRoutes } from "@routes/urls";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import Overridable from "react-overridable";
import { Grid, Item, List } from "semantic-ui-react";
import { renderSubtitle } from "@modules/Document/utils";

const SpecialCollectionAuthors = ({ authors, prefix = "" }) => {
  if (!authors || authors.length === 0) return null;

  const displayAuthors = authors.slice(
    0,
    invenioConfig.LITERATURE.authors.maxDisplay
  );
  const hasMoreAuthors =
    authors.length > invenioConfig.LITERATURE.authors.maxDisplay;

  return (
    <span>
      {prefix}
      {displayAuthors.map((author, index) => (
        <span key={index}>
          {author.full_name}
          {index < displayAuthors.length - 1 ? ", " : ""}
        </span>
      ))}
      {hasMoreAuthors && " et al."}
    </span>
  );
};

SpecialCollectionAuthors.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      full_name: PropTypes.string,
    })
  ),
  prefix: PropTypes.string,
};

SpecialCollectionAuthors.defaultProps = {
  authors: [],
  prefix: "",
};

const YearAdviserInfo = ({ publicationYear, advisers }) => {
  if (!publicationYear && (!advisers || advisers.length === 0)) return null;

  return (
    <List>
      {publicationYear && (
        <List.Item>
          <List.Content>
            <span>Completion Year </span>
            {publicationYear}
          </List.Content>
        </List.Item>
      )}
      {advisers && advisers.length > 0 && (
        <List.Item>
          <List.Content>
            <span>Adviser{advisers.length > 1 ? "s" : ""}: </span>
            {advisers.map((adviser, index) => (
              <span key={index}>
                {adviser.full_name}
                {index < advisers.length - 1 ? ", " : ""}
              </span>
            ))}
          </List.Content>
        </List.Item>
      )}
    </List>
  );
};

YearAdviserInfo.propTypes = {
  publicationYear: PropTypes.string,
  advisers: PropTypes.arrayOf(
    PropTypes.shape({
      full_name: PropTypes.string,
    })
  ),
};

const DepartmentProgramInfo = ({ academicInfo }) => {
  if (_isEmpty(academicInfo)) return null;

  return (
    <List>
      {academicInfo?.department && (
        <List.Item>
          <List.Content>
            <span>Department: </span>
            {academicInfo.department}
          </List.Content>
        </List.Item>
      )}
      {academicInfo?.program && (
        <List.Item>
          <List.Content>
            <span>Program: </span>
            {academicInfo.program}
          </List.Content>
        </List.Item>
      )}
    </List>
  );
};

DepartmentProgramInfo.propTypes = {
  academicInfo: PropTypes.shape({
    department: PropTypes.string,
    program: PropTypes.string,
    campus: PropTypes.string,
  }),
};

const getCollectionTypeLabel = (collectionType) => {
  if (!collectionType) return "SPECIAL COLLECTION";

  // Handle the case where the data might already be in display format
  if (collectionType.includes(" ")) {
    // Convert from "UNDERGRADUATE THESIS" to "Undergraduate Thesis"
    return collectionType
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Handle underscore-separated values
  const typeLabels = {
    UNDERGRADUATE_THESIS: "Undergraduate Thesis",
    GRADUATE_THESIS: "Graduate Thesis",
    DISSERTATION: "Dissertation",
    ENCYCLOPEDIA: "Encyclopedia",
    HANDBOOK: "Handbook",
    OTHER: "Other",
  };

  return typeLabels[collectionType] || collectionType;
};

const SpecialCollectionListEntry = ({ metadata }) => {
  const detailsUrl = FrontSiteRoutes.specialCollectionDetailsFor(metadata.pid);
  const isRestricted = _get(metadata, "restricted", false);
  const coverUrl = _get(metadata, "cover_metadata.urls.medium");

  return (
    <Item>
      <Item.Image floated="left" as={Link} to={detailsUrl}>
        <LiteratureCover
          isRestricted={isRestricted}
          size="small"
          url={coverUrl}
        />
      </Item.Image>

      <Item.Content>
        <Item.Meta>
          {getCollectionTypeLabel(metadata.collection_type)}
        </Item.Meta>

        <Item.Header as={Link} to={detailsUrl}>
          <LiteratureTitle title={metadata.title} />
        </Item.Header>

        <Overridable
          id="SpecialCollectionListEntry.BeforeAuthors"
          metadata={metadata}
        />

        <Item.Meta>
          {renderSubtitle(metadata?.alternative_titles)}
          <SpecialCollectionAuthors authors={metadata.authors} prefix="by " />
        </Item.Meta>

        <Item.Description>
          <Truncate lines={2}>{metadata.abstract}</Truncate>
        </Item.Description>

        <Item.Meta>
          <Grid columns="equal">
            <Grid.Column>{/* Left column - empty as requested */}</Grid.Column>

            <Grid.Column>
              <YearAdviserInfo
                publicationYear={metadata.publication_year}
                advisers={metadata.advisers}
              />
            </Grid.Column>

            <Grid.Column>
              <DepartmentProgramInfo academicInfo={metadata.academic_info} />
            </Grid.Column>

            <Grid.Column>
              {/* Empty column for consistent spacing */}
            </Grid.Column>
          </Grid>
        </Item.Meta>

        <Item.Extra>
          <LiteratureTags tags={metadata.tags} />
        </Item.Extra>
      </Item.Content>
    </Item>
  );
};

SpecialCollectionListEntry.propTypes = {
  metadata: PropTypes.object.isRequired,
};

export default Overridable.component(
  "SpecialCollectionListEntry",
  SpecialCollectionListEntry
);
